import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";
import { parseDateAsLocal } from "src/utils/formatUtils";

import {
  Grid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import ReactDOM from "react-dom";
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";

import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Error from "src/components/Error";

type ReminderDTO = {
  id: string;
  acctId: string;
  reminderNote: string;
  reminderDate: Date | null;
  status: string;
  reminderType: string;
  reminderFrequency: string;
};

interface AccountReminderListProps {
  acctId?: string;
  isModal?: boolean;
  id?: string;
}

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: any) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};
interface PageState {
  skip: number;
  take: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};
const initialPage: PageState = { skip: 0, take: 20 };

// Custom filter function for tags that supports array contains operation
const customFilterFunction = (
  item: ReminderDTO,
  filter: FilterDescriptor | CompositeFilterDescriptor,
): boolean => {
  if (!filter) {
    return true;
  }

  // Handle composite filters
  if ("logic" in filter && filter.filters) {
    if (filter.logic === "and") {
      return filter.filters.every((f) => customFilterFunction(item, f));
    } else {
      return filter.filters.some((f) => customFilterFunction(item, f));
    }
  }

  return true;
};

interface CompletedCellProps {
  dataItem: ReminderDTO;
  field?: string;
}
interface SetReminderCompleteDTO {
  id: string;
  acctId: string;
}

const AccountReminderList: FC<AccountReminderListProps> = ({
  acctId: propAcctId,
  isModal = false,
  id: propId,
}) => {
  const [searchParams] = useSearchParams();
  const urlAcctId = searchParams.get("acctId");
  const acctId = propAcctId || urlAcctId;
  const urlId = searchParams.get("id");
  const id = propId || urlId;
  const [reminders, setReminders] = useState<ReminderDTO[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const gridRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: "and",
    filters: [],
  });

  // Update filter when id or acctId changes
  useEffect(() => {
    const newFilters = [];

    if (id) {
      newFilters.push({
        field: "id",
        operator: "eq",
        value: id,
      });
    }

    if (acctId) {
      newFilters.push({
        field: "acctId",
        operator: "contains",
        value: acctId,
      });
    }

    setFilter({
      logic: "and",
      filters: newFilters,
    });
  }, [id, acctId]);

  const [page, setPage] = useState<PageState>(initialPage);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const loadData = useCallback(async () => {
    setShowLoading(true);
    try {
      const response = await callApi(
        "sysadmin/accounts/GetReminders",
        HttpMethod.POST,
        {},
      );
      setReminders(
        response.data.map(
          (
            r: Omit<ReminderDTO, "reminderDate"> & {
              reminderDate: string | null;
            },
          ) => ({
            ...r,
            reminderDate: parseDateAsLocal(r.reminderDate),
          }),
        ),
      );
      setShowLoading(false);
    } catch (error) {
      console.error("Error fetching GetReminders:", error);
      toast.error(`Error fetching data from GetReminders: ${error}`);
      setShowLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [acctId, loadData]);

  // Load Bootstrap icons CSS
  useEffect(() => {
    const linkId = "bootstrap-icons-reminders-modal";

    // Check if the link already exists
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href =
        "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css";
      document.head.appendChild(link);
    }

    // Cleanup function to remove the CSS when component unmounts
    return () => {
      const existingLink = document.getElementById(linkId);
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, []);

  const transformDateEqualsToRange = (
    filter: CompositeFilterDescriptor,
  ): CompositeFilterDescriptor => {
    if (!filter || !Array.isArray(filter.filters)) {
      return { logic: "and", filters: [] };
    }

    const transform = (
      f: FilterDescriptor | CompositeFilterDescriptor,
    ): (FilterDescriptor | CompositeFilterDescriptor)[] => {
      if ("logic" in f && Array.isArray(f.filters)) {
        return [
          {
            ...f,
            filters: f.filters.flatMap(transform),
          },
        ];
      }

      const fd = f as FilterDescriptor;

      if (fd.field === "reminderDate" && fd.operator === "eq" && fd.value) {
        const raw = new Date(fd.value); // incoming UTC string
        const localMidnight = new Date(
          raw.getFullYear(),
          raw.getMonth(),
          raw.getDate(),
        );
        const nextDay = new Date(localMidnight);
        nextDay.setDate(nextDay.getDate() + 1);

        return [
          { field: fd.field, operator: "gte", value: localMidnight },
          { field: fd.field, operator: "lt", value: nextDay },
        ];
      }

      return [fd];
    };

    return {
      logic: filter.logic,
      filters: filter.filters.flatMap(transform),
    };
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    const incomingFilter = e.filter as CompositeFilterDescriptor | null;

    if (!incomingFilter || !incomingFilter.filters) {
      setFilter(initialFilter); // or keep existing filter
      return;
    }

    const transformed = transformDateEqualsToRange(incomingFilter);
    setFilter(transformed);
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? reminders.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const statusPriority = (status: string | undefined): number => {
    switch (status) {
      case "Today":
        return 0;
      case "Upcoming":
        return 1;
      case "Past Due":
        return 2;
      case "Completed":
        return 3;
      default:
        return 4;
    }
  };

  // Apply custom filtering and sorting
  const filteredData = useMemo(() => {
    let data = reminders;
    if (filter && filter.filters && filter.filters.length > 0) {
      data = filterBy(reminders, filter);
    }

    return [...data].sort((a, b) => {
      return statusPriority(a.status) - statusPriority(b.status);
    });
  }, [reminders, filter]);

  const pagedData = useMemo(() => {
    const state: {
      skip: number;
      take: number;
    } = {
      skip: page.skip ?? 0,
      take: page.take ?? 20,
    };

    return filteredData.slice(state.skip, state.skip + state.take);
  }, [filteredData, page]);

  const statusColor = (status: string | undefined): string => {
    switch (status) {
      case "Past Due":
        return "#dc3545"; // red
      case "Today":
        return "#ffc107"; // yellow
      case "Upcoming":
        return "#28a745"; // green
      case "Completed":
        return "#007bff"; // blue
      default:
        return "#6c757d"; // gray
    }
  };

  const StatusCell: FC<any> = (props) => {
    const status = props.dataItem[props.field];
    const color = statusColor(status);

    return (
      <td>
        <span
          style={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: color,
            marginRight: "8px",
            verticalAlign: "middle",
          }}
        ></span>
        <span>{status}</span>
      </td>
    );
  };

  const AcctIdCell: FC<any> = (props) => {
    const acctId = props.dataItem[props.field];
    return (
      <td>
        <a
          href={`${runtimeConfig.classicUrl}/app/sysadmin/accounts.adp?acctId=${acctId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#007bff",
            textDecoration: "underline",
            fontWeight: "bold",
          }}
        >
          {acctId}
        </a>
      </td>
    );
  };

  const CompletedCell: FC<CompletedCellProps> = useCallback(
    ({ dataItem }) => {
      const handleCompleteClick = async () => {
        setShowLoading(true);
        const accountToUpdate: SetReminderCompleteDTO = {
          id: dataItem.id,
          acctId: dataItem.acctId,
        };

        try {
          const response = await callApi(
            "sysadmin/accounts/SetReminderComplete",
            HttpMethod.POST,
            accountToUpdate,
          );
          toast.success(response.message);
          setShowLoading(false);
          // Reload data after successful completion
          loadData();
        } catch (error) {
          setErrorMessage(
            `There's an error completing the reminder. Reminder Note: '${dataItem.reminderNote}'`,
          );
          setShowLoading(false);
        }
      };

      return (
        <td>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleCompleteClick}
            type="button"
            title={`Complete reminder for ${dataItem.acctId}`}
            disabled={showLoading}
          >
            ✓ Completed
          </button>
        </td>
      );
    },
    [loadData, showLoading],
  );

  return (
    <>
      {/* uncomment for debugging {filter && (
        <div className="alert alert-info">
          Filter for debugging: {JSON.stringify(filter)}
        </div>
      )} */}
      <div ref={gridRef} style={{ padding: "10px" }}>
        {showLoading ? <LoadingPanel gridRef={gridRef} /> : null}
        {errorMessage && <Error message={errorMessage} />}

        <Grid
          className={`sysadmin-grid ${isModal ? "sysadmin-grid-modal" : ""}`}
          data={pagedData}
          dataItemKey={"id"}
          filterable={true}
          sortable={true}
          skip={page.skip}
          take={page.take}
          total={filteredData.length}
          pageable={{
            buttonCount: 4,
            pageSizes: [10, 20, 30, 40],
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          filter={filter}
          onFilterChange={handleFilterChange}
        >
          <Column
            field="acctId"
            title="Account ID"
            cells={{ data: AcctIdCell }}
          />
          <Column field="reminderNote" title="Reminder Note" />
          <Column
            field="reminderDate"
            title="Reminder Date"
            filter="date"
            format="{0:d}"
          />
          <Column field="status" title="Status" cells={{ data: StatusCell }} />
          <Column field="reminderType" title="Reminder Type" />
          <Column field="reminderFrequency" title="Reminder Frequency" />
          <Column
            title="Actions"
            cells={{ data: CompletedCell }}
            filterable={false}
            sortable={false}
            width="150px"
          />
        </Grid>
      </div>
    </>
  );
};

export default AccountReminderList;
